import { Link } from "react-router-dom";
import { Linkedin, Facebook, MessageCircle } from "lucide-react";

function XIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

const SOCIAL_GROUPS = [
  {
    brand: "TimePe",
    links: [
      { label: "LinkedIn", href: "https://www.linkedin.com/company/timepe.bharat", Icon: Linkedin },
      { label: "Facebook", href: "https://www.facebook.com/timepe.bharat", Icon: Facebook },
      { label: "X", href: "https://x.com/timepe_bharat", Icon: XIcon },
    ],
  },
  {
    brand: "Avyagrah",
    links: [
      { label: "LinkedIn", href: "https://www.linkedin.com/company/avyagrahtech", Icon: Linkedin },
      { label: "Facebook", href: "https://www.facebook.com/avyagrahtech", Icon: Facebook },
      { label: "X", href: "https://x.com/avyagrahtech", Icon: XIcon },
    ],
  },
];

const iconBtn =
  "flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-panel text-mute transition-colors duration-300 hover:border-electric/50 hover:text-electric";

const iconBtnLightBox =
  "flex h-9 w-9 items-center justify-center rounded-lg border border-transparent bg-[#F4F7FB] text-[#0A1628] shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-colors duration-300 hover:bg-electric hover:text-white";

export function SocialLinks({ lightBox = false }) {
  const btn = lightBox ? iconBtnLightBox : iconBtn;
  return (
    <div className="space-y-4" data-testid="social-links">
      {SOCIAL_GROUPS.map((g) => (
        <div key={g.brand} className="flex items-center gap-4">
          <span className="w-20 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">{g.brand}</span>
          <div className="flex gap-2">
            {g.links.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${g.brand} on ${label}`}
                data-testid={`social-${g.brand.toLowerCase()}-${label.toLowerCase()}`}
                className={btn}
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      ))}
      <a
        href="https://wa.me/919279029610"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="social-whatsapp"
        className="group flex items-center gap-3 pt-1 text-sm font-medium text-mist transition-colors duration-300 hover:text-teal"
      >
        <span className={lightBox ? `${iconBtnLightBox} group-hover:bg-teal group-hover:text-white` : `${iconBtn} group-hover:border-teal/50 group-hover:text-teal`}>
          <MessageCircle className="h-4 w-4" />
        </span>
        WhatsApp: +91 92790 29610
      </a>
    </div>
  );
}

const PLATFORM_LINKS = [
  { label: "About TimePe", path: "/about" },
  { label: "Technology", path: "/technology" },
  { label: "Solutions", path: "/solutions" },
  { label: "Innovation", path: "/innovation" },
  { label: "Trust Center", path: "/trust-center" },
  { label: "Ecosystem", path: "/ecosystem" },
  { label: "Impact", path: "/impact" },
];

const COMPANY_LINKS = [
  { label: "Partners", path: "/partners" },
  { label: "Investors", path: "/investors" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", path: "/legal/privacy" },
  { label: "Terms", path: "/legal/terms" },
  { label: "Disclaimer", path: "/legal/disclaimer" },
  { label: "Cookie Policy", path: "/legal/cookies" },
];

function FooterCol({ title, links, testid }) {
  return (
    <div>
      <h3 className="font-mono text-[11px] uppercase tracking-[0.24em] text-mute">{title}</h3>
      <ul className="mt-5 space-y-3">
        {links.map((l) => (
          <li key={l.path}>
            <Link
              to={l.path}
              data-testid={`${testid}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-mist/80 transition-colors duration-300 hover:text-electric"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="theme-dark border-t border-line bg-ink" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex flex-wrap items-center gap-4">
              <span className="logo-plate !px-4 !py-3">
                <img src="/assets/timepe-logo.png" alt="TimePe logo" className="h-11 w-auto object-contain" />
              </span>
              <span className="logo-plate !px-4 !py-3">
                <img src="/assets/avyagrah-logo.webp" alt="Avyagrah Tech Innovations logo" className="h-11 w-auto object-contain" />
              </span>
            </div>
            <p className="mt-6 font-display text-lg font-semibold text-mist">Avyagrah Tech Innovations Private Limited</p>
            <p className="mt-2 text-sm text-mute">Engineering Tomorrow with Intelligence.</p>
            <p className="mt-1 text-sm text-mute">Secure for Bharat. Built in Bharat, for Bharat.</p>
            <div className="mt-8">
              <SocialLinks lightBox />
            </div>
            <p className="mt-6 max-w-md text-xs leading-relaxed text-mute/80">
              TimePe is under active development. Capabilities described as planned or future are
              forward-looking and subject to change. This website does not claim certifications,
              licences or regulatory approvals beyond those explicitly stated.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7">
            <FooterCol title="Platform" links={PLATFORM_LINKS} testid="footer-platform" />
            <FooterCol title="Company" links={COMPANY_LINKS} testid="footer-company" />
            <FooterCol title="Legal" links={LEGAL_LINKS} testid="footer-legal" />
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-3 border-t border-line pb-28 pt-8 sm:flex-row sm:items-center sm:justify-between sm:pb-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute">
            © 2026 Avyagrah Tech Innovations Private Limited
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute/70">
            TimePe — Sovereign AI-Powered Financial Infrastructure for Bharat
          </p>
        </div>
      </div>
    </footer>
  );
}
